// Simple Notification Permission Component - Triggers Native Dialog
import React, { useState, useEffect, useCallback } from 'react';

const NotificationPermission = () => {
  const [hasShownDialog, setHasShownDialog] = useState(false);
  const [permission, setPermission] = useState('default');

  // Check notification support and current permission
  const isNotificationSupported = () => {
    return 'Notification' in window;
  };

  const getCurrentPermission = useCallback(() => {
    if (!isNotificationSupported()) return 'unsupported';
    return Notification.permission;
  }, []);

  // Request permission directly
  const requestPermission = useCallback(async () => {
    if (!isNotificationSupported()) {
      console.log('Notifications not supported in this browser');
      return 'unsupported';
    }

    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);
      return permission;
    } catch (error) {
      console.error('Error requesting permission:', error);
      return 'denied';
    }
  }, []);

  // Auto-trigger native permission dialog after 10 seconds
  useEffect(() => {
    const triggerPermission = async () => {
      const currentPermission = getCurrentPermission();
      setPermission(currentPermission);

      // Check if we've already shown the dialog before
      const hasShownBefore = localStorage.getItem('notificationPermissionShown');
      if (hasShownBefore === 'true') {
        setHasShownDialog(true);
        return;
      }

      // Only show if:
      // 1. Notifications are supported
      // 2. Permission is not already granted or denied
      // 3. We haven't shown the dialog yet
      if (isNotificationSupported() && currentPermission === 'default' && !hasShownDialog) {
        setHasShownDialog(true);
        localStorage.setItem('notificationPermissionShown', 'true');
        try {
          await requestPermission();
        } catch (error) {
          console.error('Error requesting permission:', error);
        }
      }
    };

    // Show after 10 seconds
    const timer = setTimeout(triggerPermission, 10000);
    return () => clearTimeout(timer);
  }, [hasShownDialog, getCurrentPermission, requestPermission]);

  // Don't show again if user denied permission
  useEffect(() => {
    if (permission === 'denied') {
      setHasShownDialog(true);
    }
  }, [permission]);

  // Don't render anything - just trigger the native dialog
  return null;
};

export default NotificationPermission;